import { useFocusEffect, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import OtpScreen from 'features/otp/OtpScreen';
import useReactivationChallenges from 'features/reactivation/hooks/useReactivationChallenges';
import useReactivationResendOtp from 'features/reactivation/hooks/useReactivationResendOtp';
import type { VerifySmsChallengeResult } from 'features/reactivation/hooks/useVerifySmsChallenge';
import useVerifySmsChallenge from 'features/reactivation/hooks/useVerifySmsChallenge';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { useUserProfile } from 'shared/hooks/useUserProfile';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { useAuth } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { getTrailingDigits } from 'shared/utils/string/getTrailingDigits';

const SSN_VERIFY = 'SSN_VERIFY';

const VerifySms = () => {
  const router = useRouter();
  const { t: translatedTextOtp } = useTranslation('otp');
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);

  const { t: translatedTextReactivation } = useTranslation('reactivation', {
    keyPrefix: 'verifySms',
  });

  const { identityId, customerInfo } = useUserProfile();
  const { getVaroSessionId } = useAuth();
  const reactivationResendOtp = useReactivationResendOtp();

  const { segmentClient } = useAnalyticsConfiguration();

  const {
    isLoading: isChallengeDataLoading,
    challengeId: reactivationChallengeId,
    error: reactivationChallengeError,
    refetch: refetchChallengeData,
  } = useReactivationChallenges();

  const { verifyChallenge, isLoading: isVerifyReactivationChallengeLoading } =
    useVerifySmsChallenge();

  const lastFourDigitPhoneNumber = getTrailingDigits(customerInfo?.phone ?? '');

  const resendOtpEvent = usePressableAnalytics({
    analyticsId: 'resendOtp',
    customAttributes: {
      reactivation_flow: 'true',
      verification_type: 'phone',
    },
  });

  const verifyOtpEvent = usePressableAnalytics({
    analyticsId: 'submitOtp',
    customAttributes: {
      reactivation_flow: 'true',
      verification_type: 'phone',
    },
  });

  const handleVerifyOtp = async (code: string) => {
    verifyOtpEvent();
    if (reactivationChallengeId) {
      const result = await verifyChallenge({
        challengeId: reactivationChallengeId,
        identityId: identityId ?? '',
        acceptOptions: {
          otpOption: {
            otpCode: code,
          },
        },
      });

      if (result.data?.challengeVerified) {
        return result;
      }
      return {
        success: false,
        errorCode: result.data?.errorCode,
        message: result.data?.message,
        title: result.data?.title,
      };
    }
    return {
      success: false,
    };
  };

  // Handler passed to OtpScreen for resend
  const handleResendOtp = async () => {
    resendOtpEvent();
    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(undefined, varoSessionId);

    return await reactivationResendOtp(
      {
        challengeId: reactivationChallengeId ?? '',
      },
      headers,
    );
  };

  const handlePostSuccess = (result?: VerifySmsChallengeResult) => {
    if (result?.data?.challengeVerified) {
      void segmentClient.logEvent('Registration Step Completed', {
        step_name: 'phone_number_verification',
        reactivation_flow: 'true',
        category: 'application',
        subcategory: 'checking',
      });

      if (!result.data.challenges?.length) {
        router.push('/reactivation/review-info');
        return;
      }

      const SSNChallenge = result.data.challenges.find(
        (challenge) =>
          challenge.challengeType === SSN_VERIFY &&
          challenge.challengeState === 'PENDING',
      );

      if (SSNChallenge?.challengeId) {
        router.push({
          pathname: '/reactivation/verify-ssn',
          params: {
            challengeId: SSNChallenge.challengeId,
          },
        });
      } else {
        router.push('/reactivation/error');
      }
    }
  };

  const handleBack = () => {
    router.push('/reactivation');
  };

  useBackHandler(handleBack);

  const description = (
    <>
      {translatedTextOtp('common.description')}
      {lastFourDigitPhoneNumber && (
        <BodyBaseBoldText numberOfLines={0}>
          {lastFourDigitPhoneNumber}.
        </BodyBaseBoldText>
      )}
    </>
  );

  useStepStartedFocusAnalytics({
    analyticsId: 'smsVerificationStepStarted',
    customAttributes: {
      step_name: 'phone_number_verification',
      reactivation_flow: 'true',
    },
  });

  useFocusEffect(
    useCallback(() => {
      if (!isChallengeDataLoading) {
        setShouldAutoFocus(true);
      }

      return () => {
        setShouldAutoFocus(false);
      };
    }, [isChallengeDataLoading]),
  );

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={isChallengeDataLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          reactivationChallengeError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedTextReactivation('error.title')}
              messageBody={translatedTextReactivation('error.messageBody')}
              primaryButton={{
                title: translatedTextReactivation('error.retryButton'),
                onPress: () => {
                  void refetchChallengeData();
                },
              }}
              secondaryButton={{
                title: translatedTextReactivation('error.cancelButton'),
                onPress: () => {
                  router.push('/reactivation');
                },
              }}
            />
          ) : null
        }
      />
      <OtpScreen
        flowKey={OtpFlowKey.REACTIVATION}
        verificationType={OtpVerificationType.PHONE}
        description={description}
        // External state
        isLoading={isVerifyReactivationChallengeLoading}
        shouldAutoFocus={shouldAutoFocus}
        // Event handlers
        onVerifyOtp={handleVerifyOtp}
        onResendOtp={handleResendOtp}
        onBack={handleBack}
        onPostSuccess={handlePostSuccess}
        secondaryButtonLabel={translatedTextReactivation('secondaryButton')}
        secondaryButtonOnPress={() => {
          router.push('/reactivation/contact-support');
        }}
        secondaryButtonAnalyticsId="noPhoneNumberAccess"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default VerifySms;
